// -----------------------------------------------------------------------------
// This React panel replaces the legacy ImGUI "Element" panel.
// -----------------------------------------------------------------------------

// React imports
import React, { CSSProperties, memo, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    FormGroup,
    FormControl,
    MenuItem,
    Select,
    Input,
    Radio,
    RadioGroup,
    Slider,
    Stack,
    Tab,
    Tabs,
    Typography,
    IconButton,
    Paper,
} from '@mui/material';

// Zustant store (GUI)
import usePersistentStore, { reset as resetPersistent, useLayersBucket } from '../../store/persistent/persistentStore';
import { getViewer } from '../../../store/viewerStore';
import { useElementSlice } from '../../store/hooks';
import { ElementLayerState } from '../../store/layerState';
import { getGrid } from '../../../store/LoadingStore';

import { ControllerManager } from '@gui/controller/ControllerManager';

// Engine entry point
import { VisualizationElements } from '../../../Graphics/VisualizationElements';
import { AlignVerticalCenter01, ChevronSelectorVertical } from '@untitledui-pro/icons/line';
import SensorSelection from './SimulationItems/SensorSelection';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { Panels } from '@gui/constants/Panels';

import ActiveSensorAnalytics from './SimulationItems/ActiveSensorAnalytics';
import Rays from './SimulationItems/Rays';
import { MIXED, SIM_NOISE, SIM_WIND } from '@visualizer/types';
import Intersections from './SimulationItems/Intersections';
import LayerStandard from 'Simulations/LayerStandard';
import { getSentence } from '../../constants/sentences';
import Grid from 'Geodata/grid';

// Highlight hook
import { useAutoHighlight, highlightSx } from '../generic/useAutoHighlight';

// Density
import { getDensityRenderScale } from '@data/Simulations/pipeline/blocks/DensityBlock';
import { BlockKeys } from '@data/Simulations/pipeline/BlockCatalog';
import { VisualizationElementId } from '@rendering/types/VisualizationElements';

// ----------- HELPERS ------------------------------------------------------------
export const getDensityDefaultParams = () => ({
    isOneEveryThree: false,
});

export const getInvertDefaultParams = () => ({
    enabled: false,
});

// ----------- ELEMENT ------------------------------------------------------------
type ElementProps = {
    isCollapsed?: boolean;
    onToggleCollapse: () => void;
    panelsExpanded: Record<string, boolean>;
    style?: CSSProperties;
};

const Element: React.FC<ElementProps> = ({ isCollapsed = false, onToggleCollapse, panelsExpanded, style }) => {
    const isMixerModeRef = useRef(usePersistentStore.getState().global.data.isMixerMode);
    const isMultimodeRef = useRef(usePersistentStore.getState().global.data.multimode);
    const isMixerMode = usePersistentStore((s) => s.global.data.isMixerMode);
    const isMultimode = usePersistentStore((s) => s.global.data.multimode);
    const simulationItems = usePersistentStore((s) => s.getActiveLayerState()?.simulationItems);
    const defaultCoords = useMemo(() => Grid.indexToXYZ(Grid.firstActiveID), []);
    const { x, y, z } = simulationItems ?? { x: defaultCoords[0], y: defaultCoords[1], z: defaultCoords[2] };
    const activeSensorIndex = Grid.xyzToIndex(x, y, z);
    const sensorPoint = activeSensorIndex >= 0 ? Grid.sensorPoints[activeSensorIndex] : Grid.sensorPoints[0];
    const activeSensorId = sensorPoint.getID();
    const isCurrentSensorValid = Boolean(sensorPoint && activeSensorId !== -1);
    const [loadRays, setLoadRays] = useState<boolean>(false);
    const [loadIntersections, setLoadIntersections] = useState<boolean>(false);
    const [hasLinesForActiveLayer, setHasLinesForActiveLayer] = useState<boolean>(true);
    const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
    const activeLayerId = useLayersBucket((s) => s.activeLayerID);
    const activeLayer = getGrid().searchLayerByUID(activeLayerId);
    const simKind = activeLayer?.getSimKind();
    const isStandardLayer = activeLayer instanceof LayerStandard;
    const vectorialScalarMode = usePersistentStore((s) => s.getActiveLayerState()?.VECTORIAL_SCALAR_MODE ?? 0);
    const showSelected = usePersistentStore((s) => s.global.data.showSelected);

    // Layer slice
    const { element } = useElementSlice();

    //Density scale
    const densityScale = getDensityRenderScale(element?.ELEMENT_CURRENT_DENSITY);

    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    const setElementPanel = usePersistentStore((s) => s.setElementPanel);
    const setPanel = usePersistentStore((s) => s.setPanel);
    const [activeTab, setActiveTab] = useState<'visualization' | 'simItems'>('visualization');

    const expandedCount = Object.values(panelsExpanded).filter(Boolean).length;
    const disableToggle = !isCollapsed && expandedCount === 1;

    // handleChange: update both store + engine state
    const invertBlockId = usePersistentStore((state) => {
        const id = state.layers.activeLayerID;
        if (!id) return undefined;
        return state.layers.layerStates[id]?.pipeline.blocks.find((block) => block.type === BlockKeys.Invert)?.id;
    });
    const densityBlockId = usePersistentStore((state) => {
        const id = state.layers.activeLayerID;
        if (!id) return undefined;
        return state.layers.layerStates[id]?.pipeline.blocks.find((block) => block.type === BlockKeys.Density)?.id;
    });
    const hideZeroValuesBlockId = usePersistentStore((state) => {
        const id = state.layers.activeLayerID;
        if (!id) return undefined;
        return state.layers.layerStates[id]?.pipeline.blocks.find((block) => block.type === BlockKeys.HideZeroValues)?.id;
    });

    const handleChange = useCallback(
        <K extends keyof ElementLayerState>(key: K, value: ElementLayerState[K]) => {
            if (!element) return;
            setElementPanel({ [key]: value } as Partial<ElementLayerState>);
        },
        [setElementPanel, element, invertBlockId, pipelineController]
    );

    const handleToggleDetails = useCallback(() => {
        setDetailsOpen((prev) => !prev);
    }, []);

    // Geometries
    const geometries = useMemo(() => (VisualizationElements.geometries ?? []).filter((g) => g.visible !== false), []);
    const scalarDisabledShapeIds = useMemo(() => new Set<number>([VisualizationElementId.Quad, VisualizationElementId.Box]), []);
    const voxelShapeId = useMemo(() => geometries.find((g) => g.name === 'Voxel')?.id ?? VisualizationElementId.Voxel, [geometries]);
    const isScalarMode = vectorialScalarMode === 1;

    const applyDensityBlock = useCallback(
        (v: 0 | 1 | 2) => {
            const resolvedId = densityBlockId ?? pipelineController.getBlockIdByType(BlockKeys.Density);
            if (!resolvedId) return;
            if (v === 0) {
                pipelineController.toggleBlock(resolvedId, false);
            } else {
                pipelineController.updateBlockParams(resolvedId, {
                    enabled: true,
                    isOneEveryThree: v === 2,
                });
            }
        },
        [densityBlockId, pipelineController]
    );

    useEffect(() => {
        if (!element) return;
        if (!isScalarMode) return;
        if (!scalarDisabledShapeIds.has(Number(element.SEL_VIS_ELEMENT))) return;
        if (Number(element.SEL_VIS_ELEMENT) === Number(voxelShapeId)) return;
        handleChange('SEL_VIS_ELEMENT', voxelShapeId as ElementLayerState['SEL_VIS_ELEMENT']);
    }, [element?.SEL_VIS_ELEMENT, isScalarMode, scalarDisabledShapeIds, voxelShapeId, handleChange]);

    // Invert Block
    useEffect(() => {
        if (!element) return;
        const resolvedId = invertBlockId ?? pipelineController.getBlockIdByType(BlockKeys.Invert);
        if (!resolvedId) return;
        pipelineController.toggleBlock(resolvedId, Boolean(element.ELEMENT_INVERTED));
    }, [element?.ELEMENT_INVERTED, invertBlockId, pipelineController]);

    // Hide Zero Values Block
    useEffect(() => {
        if (!element) return;
        const resolvedId = hideZeroValuesBlockId ?? pipelineController.getBlockIdByType(BlockKeys.HideZeroValues);
        if (!resolvedId) return;
        pipelineController.toggleBlock(resolvedId, !Boolean(element.SHOW_ZERO_VALUE_POINTS));
    }, [element?.SHOW_ZERO_VALUE_POINTS, hideZeroValuesBlockId, pipelineController]);

    // Density Block
    useEffect(() => {
        if (!element) return;
        applyDensityBlock(element.ELEMENT_CURRENT_DENSITY);
    }, [element?.ELEMENT_CURRENT_DENSITY, applyDensityBlock]);

    // Normalize
    // useEffect(() => {
    //     if (!element) return;
    //     getViewer().refreshFrames();
    // }, [element?.normalized]);

    // Selection
    useEffect(() => {
        VisualizationElements.drawSelectedBorder(showSelected);
    }, [showSelected]);

    // Scale
    useEffect(() => {
        if (!element) return;
        getViewer()?.refreshFrames?.();
    }, [element?.scaleFactor]);

    const updateTransparency = useCallback(
        (isMixerMode: boolean, isMultimode: boolean) => {
            if (!element) return;
            const intensity = 1 - element.TRANSPARENCY;
            if (isMixerMode && isMultimode) return;
            const frameDraw = getViewer()?.getFrameDraw?.();
            if (frameDraw?.makeTransparent) {
                const enabled = element.TRANSPARENCY > 0 && !(isMixerMode && isMultimode);
                frameDraw.makeTransparent(enabled, intensity);
            }
        },
        [element?.TRANSPARENCY, element]
    );

    useEffect(() => {
        const unsubscribeMixer = usePersistentStore.subscribe(
            (state) => state.global.data.isMixerMode,
            (value) => {
                isMixerModeRef.current = value;
                updateTransparency(value, isMultimodeRef.current);
            }
        );
        const unsubscribeMultimode = usePersistentStore.subscribe(
            (state) => state.global.data.multimode,
            (value) => {
                isMultimodeRef.current = value;
                updateTransparency(isMixerModeRef.current, value);
            }
        );
        return () => {
            unsubscribeMixer();
            unsubscribeMultimode();
        };
    }, [updateTransparency]);

    useEffect(() => {
        updateTransparency(isMixerModeRef.current, isMultimodeRef.current);
    }, [updateTransparency]);

    useEffect(() => {
        if (!sensorPoint) return;
        const position = sensorPoint.getCenter?.();
        if (position) {
            VisualizationElements.moveSelectedBorder(position, densityScale);
        }
    }, [sensorPoint, densityScale]);

    useEffect(() => {
        // Mixed layers do not expose Lines: hide the section and skip any lines-size API check.
        setHasLinesForActiveLayer(simKind !== MIXED);
    }, [activeLayerId, simKind]);

    // ----------- AUTO-HIGHLIGHT HOOKS --------------------------------------------

    const ahShape = useAutoHighlight(element?.SEL_VIS_ELEMENT);
    const ahDensity = useAutoHighlight(element?.ELEMENT_CURRENT_DENSITY);
    const ahSpin = useAutoHighlight(element?.ELEMENT_SPIN);

    const ahSelected = useAutoHighlight(showSelected);
    const ahNormalized = useAutoHighlight(element?.normalized);

    const ahInvert = useAutoHighlight(element?.ELEMENT_INVERTED);

    const ahScale = useAutoHighlight(element?.scaleFactor);
    const ahTransparency = useAutoHighlight(element?.TRANSPARENCY);

    // ----------- HANDLERS ------------------------------------------------------------

    const onReset = () => {
        resetPersistent({ scope: 'layerPanel', slice: 'element' });
        setPanel({ showSelected: true }, Panels.DATA);
    };

    const onDensity = (v: 0 | 1 | 2) => {
        if (!element) return;
        const flags =
            v === 0
                ? { ELEMENT_1TO3: false, ELEMENT_1EV3: false }
                : v === 1
                  ? { ELEMENT_1TO3: true, ELEMENT_1EV3: false }
                  : { ELEMENT_1TO3: false, ELEMENT_1EV3: true };

        setElementPanel({ ELEMENT_CURRENT_DENSITY: v, ...flags });
        applyDensityBlock(v);
    };

    const handleScaleChange = useCallback(
        (value: number) => {
            const snappedValue = Math.abs(value - 1) <= 0.01 ? 1 : value;
            handleChange('scaleFactor', snappedValue);
        },
        [handleChange]
    );

    const resetScale = useCallback(() => {
        handleChange('scaleFactor', 1);
    }, [handleChange]);

    return (
        <div style={style} className={`data-panel data-panel--element ${isCollapsed ? 'data-panel--collapsed' : ''}`}>
            <div className="data-panel-tabs">
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, value) => {
                                if (isCollapsed) return;
                                const next = value as 'visualization' | 'simItems';
                                setActiveTab(next);
                            }}
                            sx={{
                                '& .MuiTabs-flexContainer': {
                                    gap: 1.5,
                                    mr: 2,
                                },
                            }}
                            aria-label="Element panel tabs"
                        >
                            <Tab value="visualization" label="VISUALIZATION" disabled={isCollapsed} />
                            <Tab value="simItems" label="SIM ITEMS" disabled={isCollapsed} />
                        </Tabs>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={onToggleCollapse}
                        disabled={disableToggle}
                        sx={{
                            position: 'absolute',
                            right: -8,
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                boxShadow: 'none',
                                backgroundColor: 'transparent',
                            },
                            '&:active': {
                                boxShadow: 'none',
                            },
                            '&:focus-visible': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                transition: 'transform 120ms ease',
                                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                            }}
                        >
                            {isCollapsed ? (
                                <ChevronSelectorVertical className="icon-size-small" />
                            ) : (
                                <AlignVerticalCenter01 className="icon-size-small" />
                            )}
                        </Box>
                    </IconButton>
                </Box>
            </div>

            {!isCollapsed && (
                <div className="data-panel-content">
                    <div className="data-panel-scroll">
                        {activeTab === 'visualization' && (
                            <Box sx={{ display: 'grid', pr: 0.5 }}>
                                <Stack sx={{ width: '100%', alignItems: 'stretch' }}>
                                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Button variant="contained" size="small" onClick={onReset}>
                                            Reset
                                        </Button>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            columnGap: 1,
                                            justifyItems: 'start',
                                            alignItems: 'start',
                                            width: '100%',
                                        }}
                                    >
                                        {/* SHAPE */}
                                        <Box className="panel-control-section" ref={ahShape.ref} sx={highlightSx()}>
                                            <Typography sx={{ fontWeight: 'bold', width: '100%' }}>Shape</Typography>
                                            <RadioGroup
                                                value={element?.SEL_VIS_ELEMENT}
                                                onChange={(_, v) =>
                                                    handleChange('SEL_VIS_ELEMENT', Number(v) as ElementLayerState['SEL_VIS_ELEMENT'])
                                                }
                                                className="panel-compact-control-stack"
                                                onPointerDownCapture={ahShape.markManual}
                                                onKeyDownCapture={ahShape.markManual}
                                            >
                                                {geometries.map((g: any) => (
                                                    <FormControlLabel
                                                        key={g.id}
                                                        value={g.id}
                                                        disabled={isScalarMode && scalarDisabledShapeIds.has(Number(g.id))}
                                                        control={<Radio size="small" />}
                                                        label={g.name}
                                                    />
                                                ))}
                                            </RadioGroup>
                                            {Number(element?.SEL_VIS_ELEMENT) === Number(voxelShapeId) && (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={Boolean(element?.NO_STROKES)}
                                                            onChange={(_, checked) => handleChange('NO_STROKES', checked)}
                                                        />
                                                    }
                                                    label="No strokes"
                                                />
                                            )}
                                        </Box>

                                        {/* DENSITY */}
                                        <Box className="panel-control-section" ref={ahDensity.ref} sx={highlightSx()}>
                                            <Typography sx={{ fontWeight: 'bold', width: '100%' }}>Density</Typography>
                                            <RadioGroup
                                                value={element?.ELEMENT_CURRENT_DENSITY}
                                                onChange={(_, v) => onDensity(Number(v) as 0 | 1 | 2)}
                                                className="panel-compact-control-stack"
                                                onPointerDownCapture={ahDensity.markManual}
                                                onKeyDownCapture={ahDensity.markManual}
                                            >
                                                <FormControlLabel value={0} control={<Radio size="small" />} label="Full" />
                                                <FormControlLabel value={1} control={<Radio size="small" />} label="1 to 3" />
                                                <FormControlLabel value={2} control={<Radio size="small" />} label="1 ev 3" />
                                            </RadioGroup>
                                        </Box>
                                    </Box>

                                    {/* OPTIONS + SPIN */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 1, mt: 0.5 }}>
                                        <Box className="panel-control-section">
                                            <Typography sx={{ fontWeight: 'bold', width: '100%' }}>Options</Typography>
                                            <FormGroup className="panel-compact-control-stack">
                                                <Box
                                                    ref={ahSelected.ref}
                                                    sx={highlightSx()}
                                                    onPointerDownCapture={ahSelected.markManual}
                                                    onKeyDownCapture={ahSelected.markManual}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={showSelected}
                                                                onChange={(_, checked) => setPanel({ showSelected: checked }, Panels.DATA)}
                                                            />
                                                        }
                                                        label="Selected"
                                                    />
                                                </Box>
                                                <Box
                                                    ref={ahInvert.ref}
                                                    sx={highlightSx()}
                                                    onPointerDownCapture={ahInvert.markManual}
                                                    onKeyDownCapture={ahInvert.markManual}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={element?.ELEMENT_INVERTED}
                                                                onChange={(_, c) => handleChange('ELEMENT_INVERTED', c)}
                                                            />
                                                        }
                                                        label="Invert Directions"
                                                    />
                                                </Box>

                                                <Box
                                                    ref={ahNormalized.ref}
                                                    sx={highlightSx()}
                                                    onPointerDownCapture={ahNormalized.markManual}
                                                    onKeyDownCapture={ahNormalized.markManual}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={element?.normalized}
                                                                onChange={(_, c) => handleChange('normalized', c)}
                                                            />
                                                        }
                                                        label="Fixed Size"
                                                    />
                                                </Box>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={!Boolean(element?.SHOW_ZERO_VALUE_POINTS)}
                                                            onChange={(_, checked) => handleChange('SHOW_ZERO_VALUE_POINTS', !checked)}
                                                        />
                                                    }
                                                    label="Hide Zero"
                                                />
                                            </FormGroup>
                                            <Box sx={{ mt: 0.5, display: 'grid', gap: 0.5 }}>
                                                <Typography sx={{ fontWeight: 'bold', width: '100%' }}>Sensor Colors</Typography>
                                                <FormControl size="small" fullWidth>
                                                    <Select
                                                        value={element?.SENSOR_COLOR_MODE ?? 0}
                                                        onChange={(event) =>
                                                            handleChange(
                                                                'SENSOR_COLOR_MODE',
                                                                Number(event.target.value) as ElementLayerState['SENSOR_COLOR_MODE']
                                                            )
                                                        }
                                                        input={<Input disableUnderline />}
                                                    >
                                                        <MenuItem value={0}>Color Mapping</MenuItem>
                                                        <MenuItem value={1}>Grey Mapping</MenuItem>
                                                        <MenuItem value={2}>All Grey</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>

                                        <Box className="panel-control-section" ref={ahSpin.ref} sx={highlightSx()}>
                                            <CustomTooltip side="left" mode="icon" title={getSentence('Visualization.Spin.Tooltip')}>
                                                <Typography sx={{ fontWeight: 'bold' }}>Spin</Typography>
                                            </CustomTooltip>
                                            <RadioGroup
                                                value={element?.ELEMENT_SPIN}
                                                onChange={(_, v) => handleChange('ELEMENT_SPIN', Number(v) as 0 | 1)}
                                                className="panel-compact-control-stack"
                                                onPointerDownCapture={ahSpin.markManual}
                                                onKeyDownCapture={ahSpin.markManual}
                                            >
                                                <FormControlLabel value={0} control={<Radio size="small" />} label="Spin 1" />
                                                <FormControlLabel value={1} control={<Radio size="small" />} label="Spin 2" />
                                            </RadioGroup>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ mb: 1, mt: 1 }} />

                                    {/* SCALE */}
                                    <Box
                                        ref={ahScale.ref}
                                        sx={[
                                            {
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                pr: 1.7,
                                                gap: 0.5,
                                                mt: 1,
                                            },
                                            highlightSx(),
                                        ]}
                                    >
                                        <Typography sx={{ minWidth: 80 }}>Scale</Typography>
                                        <Slider
                                            className="panel-slider"
                                            size="small"
                                            min={0}
                                            valueLabelDisplay="on"
                                            max={2}
                                            step={0.01}
                                            value={element?.scaleFactor}
                                            onChange={(_, v) => handleScaleChange(v as number)}
                                            onPointerDownCapture={ahScale.markManual}
                                            onKeyDownCapture={ahScale.markManual}
                                        />
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={resetScale}
                                            disabled={element?.scaleFactor === 1}
                                            sx={{ minWidth: 56, px: 1 }}
                                        >
                                            Reset
                                        </Button>
                                    </Box>

                                    {/* TRANSPARENCY */}
                                    {!(isMixerMode && isMultimode) && (
                                        <Box
                                            ref={ahTransparency.ref}
                                            sx={[
                                                {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    pr: 1.7,
                                                    gap: 0.5,
                                                },
                                                highlightSx(),
                                            ]}
                                        >
                                            <Typography sx={{ minWidth: 80 }}>Transparency</Typography>
                                            <Slider
                                                className="panel-slider"
                                                size="small"
                                                min={0}
                                                max={1}
                                                valueLabelDisplay="on"
                                                step={0.01}
                                                value={element?.TRANSPARENCY}
                                                onChange={(_, v) => handleChange('TRANSPARENCY', v as number)}
                                                onPointerDownCapture={ahTransparency.markManual}
                                                onKeyDownCapture={ahTransparency.markManual}
                                            />
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        )}
                        {
                            <Box sx={{ display: activeTab == 'visualization' ? 'none' : '' }}>
                                {isCurrentSensorValid ? (
                                    <Box>
                                        <ActiveSensorAnalytics
                                            activeSensorID={activeSensorId}
                                            detailsOpen={detailsOpen}
                                            onToggleDetails={handleToggleDetails}
                                        />
                                    </Box>
                                ) : (
                                    <Typography sx={{ mb: 0.5, mt: 0.5 }} color="red">
                                        Move selected Sensor Point inside the Grid
                                    </Typography>
                                )}

                                <SensorSelection />
                                {isStandardLayer && (
                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                        {hasLinesForActiveLayer && (
                                            <Paper className="sim-items-section" sx={{ pl: 0.1, borderRadius: 2 }}>
                                                <Rays
                                                    activeSensorId={activeSensorId}
                                                    isCurrentSensorValid={isCurrentSensorValid}
                                                    loadRays={loadRays}
                                                    setLoadRays={setLoadRays}
                                                    onLinesAvailabilityChange={setHasLinesForActiveLayer}
                                                />
                                            </Paper>
                                        )}
                                        {simKind !== SIM_WIND && simKind !== SIM_NOISE && simKind !== MIXED && (
                                            <Paper className="sim-items-section" sx={{ pl: 0.1, borderRadius: 2 }}>
                                                <Intersections
                                                    activeSensorId={activeSensorId}
                                                    isCurrentSensorValid={isCurrentSensorValid}
                                                    loadIntersections={loadIntersections}
                                                    setLoadIntersections={setLoadIntersections}
                                                    loadRays={loadRays}
                                                />
                                            </Paper>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        }
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(Element);
